export type DeductionMethod = 'installment' | 'nextPayday' | 'firstOfMonth'

export type AdvanceCategoryDef = {
  key: string
  label: string
  deductionMethod?: DeductionMethod
}

export const DEDUCTION_METHODS: DeductionMethod[] = ['installment', 'nextPayday', 'firstOfMonth']

export const DEDUCTION_METHOD_OPTIONS: { value: DeductionMethod; label: string }[] = [
  { value: 'installment', label: 'EEAP ($60 per payday)' },
  { value: 'nextPayday', label: 'Next payday' },
  { value: 'firstOfMonth', label: 'First pay period of the month' },
]

export function defaultDeductionMethod(key: string): DeductionMethod {
  return String(key || '').trim() === 'eeap' ? 'installment' : 'nextPayday'
}

export function normalizeDeductionMethod(raw: unknown, key = ''): DeductionMethod {
  const value = String(raw || '').trim()
  if (value === 'installment' || value === 'eeap') return 'installment'
  if (value === 'nextPayday' || value === 'advance' || value === 'lump') return 'nextPayday'
  if (value === 'firstOfMonth') return 'firstOfMonth'
  return defaultDeductionMethod(key)
}

export const DEFAULT_ADVANCE_CATEGORY_DEFS: AdvanceCategoryDef[] = [
  { key: 'eeap', label: 'EEAP', deductionMethod: 'installment' },
  { key: 'eeapAdvance', label: 'EEAP Advance', deductionMethod: 'nextPayday' },
  { key: 'advance', label: 'Advance', deductionMethod: 'nextPayday' },
]

export const DEFAULT_ADVANCE_CATEGORY_LABELS: Record<string, string> = Object.fromEntries(
  DEFAULT_ADVANCE_CATEGORY_DEFS.map((item) => [item.key, item.label]),
)

export const MAX_ADVANCE_CATEGORIES = 20
export const MAX_ADVANCE_CATEGORY_LABEL_LENGTH = 80

export function ledgerCategoryLabel(category: string, catalog?: AdvanceCategoryDef[] | null): string {
  const key = String(category || '').trim()
  if (!key) return ''
  const match = catalog?.find((item) => item.key === key)
  if (match?.label) return match.label
  return DEFAULT_ADVANCE_CATEGORY_LABELS[key] || key
}

export function parseAdvanceCategoryDefs(raw: unknown): AdvanceCategoryDef[] {
  return Array.isArray(raw)
    ? raw
        .map((item) => {
          const key = String(item?.key || '').trim()
          const label = String(item?.label || '').trim()
          return {
            key,
            label,
            deductionMethod: normalizeDeductionMethod(item?.deductionMethod, key),
          }
        })
        .filter((item) => item.key && item.label)
    : []
}

export function mergeLedgerCategoryOptions(
  catalog: AdvanceCategoryDef[],
  extraKeys: Iterable<string> = [],
): AdvanceCategoryDef[] {
  const base = catalog.length ? catalog : DEFAULT_ADVANCE_CATEGORY_DEFS
  const seen = new Set<string>()
  const out: AdvanceCategoryDef[] = []
  for (const item of base) {
    const key = String(item.key || '').trim()
    if (!key || seen.has(key)) continue
    seen.add(key)
    out.push({
      key,
      label: item.label || ledgerCategoryLabel(key, catalog),
      deductionMethod: normalizeDeductionMethod(item.deductionMethod, key),
    })
  }
  for (const extra of extraKeys) {
    const key = String(extra || '').trim()
    if (!key || seen.has(key)) continue
    seen.add(key)
    out.push({
      key,
      label: ledgerCategoryLabel(key, catalog),
      deductionMethod: defaultDeductionMethod(key),
    })
  }
  return out
}
